import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Play, Pause, Users, Share2, Radio, ChevronDown,
  ExternalLink, Instagram, Facebook, Youtube, Clock, X, User
} from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import audioBus from "../services/AudioBus";
import { useStreamResilience } from "../hooks/useStreamResilience";
import SEO from "../components/SEO";
import NewsTicker from "../components/player/NewsTicker";
import StationPlayerCard from "../components/station/StationPlayerCard";
import {
  WeatherWidget, OnAirWidget, FollowWidget, SponsorWidget,
  ScheduleWidget, DonationsWidget, MerchWidget, SocialLinksWidget,
  TopFansWidget, ContactWidget, LaunchCtaWidget
} from "../components/station/SidebarWidgets";
import {
  HistorySection, ChatSection, RequestsSection, AboutSection, LiveVideoSection
} from "../components/station/ContentSections";

const StationPage = () => {
  const { slug } = useParams();
  const { user, token } = useAuth();

  // Core state
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [mounts, setMounts] = useState([]);
  const [selectedMount, setSelectedMount] = useState(null);
  const [showMountDropdown, setShowMountDropdown] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const streamUrlRef = useRef(null);

  // Interactive state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [requestText, setRequestText] = useState("");
  const [dedicateText, setDedicateText] = useState("");
  const [dedicateTo, setDedicateTo] = useState("");
  const [requestSuccess, setRequestSuccess] = useState("");
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherForecast, setWeatherForecast] = useState([]);
  const [currentTime, setCurrentTime] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followEmail, setFollowEmail] = useState("");
  const [followLoading, setFollowLoading] = useState(false);
  const [scheduledShows, setScheduledShows] = useState([]);
  const [onAirShow, setOnAirShow] = useState(null);
  const [djProfiles, setDjProfiles] = useState([]);
  const [viewingDJ, setViewingDJ] = useState(null);

  // Nav state
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const animationRef = useRef(null);
  const audioBusHandle = useRef(null);
  const { isBuffering } = useStreamResilience(audioRef);

  // ── Data Fetching ──

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/whitelabel/player/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        // Lock stream URL on first load only
        if (!streamUrlRef.current && data.stream_url) {
          streamUrlRef.current = data.stream_url;
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => {
    fetchConfig();
    const interval = setInterval(fetchConfig, 10000);
    return () => {
      clearInterval(interval);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioBusHandle.current) audioBusHandle.current.unregister();
    };
  }, [fetchConfig]);

  // Fetch mounts
  useEffect(() => {
    if (!config?.azuracast_station_id) return;
    const fetchMounts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/listen/${config.azuracast_station_id}/mounts`);
        if (res.ok) {
          const data = await res.json();
          if (data.mounts?.length) {
            setMounts(data.mounts);
            if (!selectedMount) {
              setSelectedMount(data.mounts[0]);
              streamUrlRef.current = data.mounts[0].url;
            }
          }
        }
      } catch (e) {}
    };
    fetchMounts();
  }, [config?.azuracast_station_id]);

  // Weather
  useEffect(() => {
    if (!config?.show_weather) { setWeatherLoading(false); return; }
    let cancelled = false;
    const fetchWeather = async () => {
      // Try backend API first
      try {
        let url = `${BACKEND_URL}/api/weather`;
        if (config.weather_city) url += `?city=${encodeURIComponent(config.weather_city)}`;
        const res = await fetch(url);
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (!data.error) {
            if (!data.city && config.weather_city) data.city = config.weather_city;
            setWeather(data);
            setWeatherLoading(false);
            // Still fetch hourly forecast from Open-Meteo
          }
        }
      } catch (err) { console.warn("Backend weather failed:", err); }

      // Fallback: fetch directly from open-meteo (no CORS issues, no API key)
      try {
        const city = config.weather_city || "Nashville";
        const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1`);
        if (geoRes.ok) {
          const geoData = await geoRes.json();
          const loc = geoData.results?.[0];
          if (loc && !cancelled) {
            const wxRes = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}` +
              `&current=temperature_2m,relative_humidity_2m,apparent_temperature,wind_speed_10m,weather_code` +
              `&hourly=temperature_2m,weather_code` +
              `&forecast_days=1` +
              `&temperature_unit=fahrenheit&wind_speed_unit=mph`
            );
            if (wxRes.ok && !cancelled) {
              const wx = await wxRes.json();
              const c = wx.current || {};
              const wmo = c.weather_code || 0;
              const descMap = {0:"Clear sky",1:"Mainly clear",2:"Partly cloudy",3:"Overcast",
                45:"Foggy",51:"Light drizzle",61:"Light rain",63:"Rain",65:"Heavy rain",
                71:"Light snow",73:"Snow",80:"Rain showers",95:"Thunderstorm"};
              // Only set current weather if TWC didn't already provide it
              if (!weather) {
                setWeather({
                  temp_f: String(Math.round(c.temperature_2m || 0)),
                  feels_like_f: String(Math.round(c.apparent_temperature || 0)),
                  humidity: String(c.relative_humidity_2m || 0),
                  wind_mph: String(Math.round(c.wind_speed_10m || 0)),
                  description: descMap[wmo] || "Unknown",
                  city: loc.name || city,
                  weather_code: wmo,
                });
                setWeatherLoading(false);
              }
              // Parse hourly forecast — find current hour, pick next 5
              const hourly = wx.hourly || {};
              const hTemps = hourly.temperature_2m || [];
              const hCodes = hourly.weather_code || [];
              const hTimes = hourly.time || [];
              const nowHour = new Date().getUTCHours();
              let si = 0;
              for (let idx = 0; idx < hTimes.length; idx++) {
                const h = parseInt(hTimes[idx]?.split("T")[1]?.split(":")[0], 10);
                if (!isNaN(h) && h >= nowHour) { si = idx; break; }
              }
              const hours = [];
              for (let i = 0; i < 5 && (si + i) < hTemps.length; i++) {
                const timeStr = hTimes[si + i] || "";
                const hr = parseInt(timeStr.split("T")[1]?.split(":")[0], 10);
                let timeLabel = i === 0 ? "Now" : "";
                if (i > 0 && !isNaN(hr)) {
                  const h12 = hr % 12 || 12;
                  timeLabel = `${h12} ${hr >= 12 ? "PM" : "AM"}`;
                }
                hours.push({
                  time: timeLabel || `+${i}h`,
                  temp: Math.round(hTemps[si + i]),
                  iconCode: hCodes[si + i],
                  condition: descMap[hCodes[si + i]] || "",
                });
              }
              setWeatherForecast(hours);
            }
          }
        }
      } catch (err) { console.warn("Frontend weather fallback failed:", err); }

      if (!cancelled) setWeatherLoading(false);
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 1800000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [config?.show_weather, config?.weather_city]);

  // Clock
  useEffect(() => {
    if (!config) return;
    const tz = config.timezone || "America/Chicago";
    const tick = () => {
      try {
        setCurrentTime(new Date().toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit" }));
      } catch {
        setCurrentTime(new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [config?.timezone]);

  // Chat polling
  useEffect(() => {
    if (!config?.show_chat) return;
    const stationId = config.azuracast_station_id || config.station_id;
    if (!stationId) return;
    const poll = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/chat/${stationId}/messages`);
        if (res.ok) setChatMessages(await res.json());
      } catch (e) {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [config?.show_chat, config?.azuracast_station_id, config?.station_id]);

  // Follow status
  useEffect(() => {
    if (!slug) return;
    const authToken = token || localStorage.getItem("token");
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    fetch(`${BACKEND_URL}/api/stations/${slug}/followers`).then(r => r.json()).then(d => setFollowerCount(d.follower_count || 0)).catch(() => {});
    if (authToken) {
      fetch(`${BACKEND_URL}/api/stations/${slug}/is-following`, { headers }).then(r => r.json()).then(d => setIsFollowing(d.following)).catch(() => {});
    }
  }, [slug, token]);

  // Show schedule + on-air + DJ profiles
  useEffect(() => {
    if (!slug) return;
    const tz = config?.timezone || "America/Chicago";
    const fetchShows = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/shows/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setScheduledShows(data.shows || []);
        }
      } catch (e) {}
      try {
        const res = await fetch(`${BACKEND_URL}/api/shows/${slug}/on-air?tz=${encodeURIComponent(tz)}`);
        if (res.ok) {
          const data = await res.json();
          setOnAirShow(data.on_air ? data.show : null);
        }
      } catch (e) {}
      try {
        const res = await fetch(`${BACKEND_URL}/api/dj-profiles/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setDjProfiles(data.profiles || []);
        }
      } catch (e) {}
    };
    fetchShows();
    const id = setInterval(fetchShows, 60000);
    return () => clearInterval(id);
  }, [slug, config?.timezone]);

  // ── Audio Controls ──

  const setupAnalyzer = () => {
    if (!audioRef.current || analyzerRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaElementSource(audioRef.current);
      const node = ctx.createAnalyser();
      node.fftSize = 64;
      source.connect(node);
      node.connect(ctx.destination);
      audioContextRef.current = ctx;
      analyzerRef.current = node;
    } catch (e) {
      // CORS blocks real FFT — CanvasVisualizer will use its built-in fallback animation
      console.warn("FFT analyzer unavailable (CORS), fallback animation active");
    }
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const url = streamUrlRef.current || config.stream_url;
      if (!url) return;
      if (audioRef.current.src !== url) {
        audioRef.current.src = url;
      }
      if (!audioBusHandle.current) {
        audioBusHandle.current = audioBus.register('station-page', {
          pause: () => { if (audioRef.current) audioRef.current.pause(); setIsPlaying(false); }
        });
      }
      audioBusHandle.current.claim();
      try {
        await audioRef.current.play();
        setupAnalyzer();
      } catch (err) {
        audioRef.current.removeAttribute("crossorigin");
        audioRef.current.src = url;
        try {
          await audioRef.current.play();
        } catch (e2) {
          console.error("Audio play failed:", e2);
        }
      }
      setIsPlaying(true);
    }
  };

  const switchMount = (mount) => {
    setSelectedMount(mount);
    setShowMountDropdown(false);
    streamUrlRef.current = mount.url;
    if (audioRef.current) {
      const wasPlaying = isPlaying;
      audioRef.current.pause();
      audioRef.current.src = mount.url;
      if (wasPlaying) {
        if (audioBusHandle.current) audioBusHandle.current.claim();
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setIsMuted(v === 0);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) audioRef.current.volume = isMuted ? volume : 0;
  };

  // ── Actions ──

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const authToken = token || localStorage.getItem("token");
    if (!authToken) return;
    const stationId = config.azuracast_station_id || config.station_id;
    try {
      await fetch(`${BACKEND_URL}/api/chat/${stationId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ message: chatInput }),
      });
      setChatInput("");
    } catch (e) {}
  };

  const sendRequest = async (type) => {
    const authToken = token || localStorage.getItem("token");
    const stationId = config.azuracast_station_id || config.station_id;
    const body = type === "request"
      ? { station_id: stationId, song: requestText, type: "request" }
      : { station_id: stationId, song: dedicateText, type: "dedication", dedicated_to: dedicateTo };
    try {
      const res = await fetch(`${BACKEND_URL}/api/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setRequestSuccess(type === "request" ? "Request sent!" : "Dedication sent!");
        setRequestText(""); setDedicateText(""); setDedicateTo("");
        setTimeout(() => setRequestSuccess(""), 3000);
      }
    } catch (e) {}
  };

  const handleFollow = async () => {
    if (isFollowing) return;
    setFollowLoading(true);
    const authToken = token || localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
    const body = { station_id: slug };
    if (!authToken && followEmail) body.email = followEmail;
    else if (!authToken) { setFollowLoading(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/stations/follow`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status === "success" || data.status === "already_following") {
        setIsFollowing(true);
        setFollowerCount(data.follower_count ?? followerCount + 1);
      }
    } catch (e) {}
    finally { setFollowLoading(false); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = config?.station_name || "Radio Station";
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch {}
    } else {
      navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── Loading / Not Found ──

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Radio className="w-10 h-10 text-white/20 animate-pulse" />
    </div>
  );

  if (!config) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <p className="text-white/40">Station not found</p>
    </div>
  );

  // ── Derived config values ──

  const pc = config.primary_color || "#a855f7";
  const sc = config.secondary_color || "#22c55e";
  const tc = config.text_color || "#FFFFFF";
  const cc = config.card_color || "#FFFFFF";
  const np = config.now_playing || {};
  const history = config.song_history || np.song_history || [];
  const stationName = config.station_name || config.player_name || "Radio Station";
  const tagline = config.tagline || "";
  const logo = config.logo_url;
  const isLive = config.is_live;
  const djName = config.live_dj_name || config.streamer_name;
  const listeners = np.listeners?.current ?? np.listeners ?? config.listeners?.current ?? 0;
  const tickerMessages = config.ticker_messages || [];

  const socials = {
    instagram: config.social_instagram, twitter: config.social_twitter,
    facebook: config.social_facebook, youtube: config.social_youtube,
    tiktok: config.social_tiktok, website: config.social_website,
  };
  const hasSocials = Object.values(socials).some(v => v);

  // Section visibility flags
  const show = {
    vis: config.show_visualizer !== false,
    chat: config.show_chat !== false,
    requests: config.show_requests !== false,
    history: config.show_recently_played !== false,
    weather: config.show_weather !== false,
    ticker: config.show_ticker !== false,
    dedicate: config.show_dedicate !== false,
    sponsor: config.show_sponsor && config.sponsor_name,
    about: config.page_show_about !== false,
    onAir: config.page_show_on_air !== false,
    newsletter: config.page_show_newsletter !== false,
    socialLinks: config.page_show_social_links !== false,
    launchCta: config.page_show_launch_cta !== false,
    schedule: config.page_show_schedule === true,
    contact: config.page_show_contact === true,
    donations: config.page_show_donations === true,
    merch: config.page_show_merchandise === true,
    liveVideo: config.page_show_live_video === true && config.video_url,
    topFans: config.show_top_fans === true,
  };

  // Nav items
  const navItems = [
    { id: "home", label: "Home" },
    { id: "nowplaying", label: "Now Playing" },
    ...(show.chat ? [{ id: "chat", label: "Chat" }] : []),
    ...(show.requests ? [{ id: "requests", label: "Requests" }] : []),
    ...(show.schedule ? [{ id: "schedule", label: "Schedule" }] : []),
    ...(show.about ? [{ id: "about", label: "About" }] : []),
    ...(show.contact ? [{ id: "contact", label: "Contact" }] : []),
  ];

  // ── Render ──

  return (
    <div className="min-h-screen" style={{ background: config.bg_color || "#050505" }}>
      <SEO
        title={stationName}
        description={`${tagline} — Listen to ${stationName} live on DJsandMCMedia. ${config.genre ? `Genre: ${config.genre}.` : ""} ${config.location ? `Broadcasting from ${config.location}.` : ""} Stream 24/7 with AutoDJ, live DJs, and more.`}
        image={logo || "/og-image.png"}
        type="music.radio_station"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RadioStation",
          "name": stationName,
          "description": tagline,
          "url": window.location.href,
          "image": logo || undefined,
          "genre": config.genre || undefined,
          "areaServed": config.location || undefined,
          "broadcastFrequency": "Internet",
          "broadcaster": {
            "@type": "Organization",
            "name": "DJsandMCMedia",
            "url": "https://djsandmcmedia.com"
          },
          ...(config.stream_url ? { "sameAs": config.stream_url } : {}),
        }}
      />
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        preload="none"
      />

      {/* ═══ NAV BAR ═══ */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "rgba(0,0,0,0.88)", borderBottom: `2px solid ${pc}` }}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt={stationName} className="h-14 w-auto object-contain" />
            ) : (
              <Radio className="w-7 h-7" style={{ color: pc }} />
            )}
            <span className="font-bold text-base hidden sm:block" style={{ color: tc }}>{stationName}</span>
          </div>

          <div className="hidden md:flex items-center gap-0.5">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg transition"
                style={activeSection === item.id ? { color: pc, background: `${pc}12` } : { color: `${tc}80` }}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {currentTime && (
              <span className="hidden sm:flex items-center gap-1.5 text-sm" style={{ color: `${tc}66`, fontVariantNumeric: "tabular-nums" }}>
                <Clock className="w-3.5 h-3.5" /> {currentTime}
              </span>
            )}
            <button
              onClick={togglePlay}
              className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
              style={{ background: pc }}
              data-testid="station-listen-live"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
              {isPlaying ? "PAUSE" : "LISTEN LIVE"}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white/50 p-1.5">
              <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenuOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 px-4 pb-3 space-y-1">
            {navItems.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)}
                className="w-full text-left px-3 py-2 text-sm rounded-lg transition"
                style={activeSection === item.id ? { color: pc, background: `${pc}12` } : { color: `${tc}80` }}>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ═══ TICKER ═══ */}
      {show.ticker && tickerMessages.length > 0 && (
        <div style={{ background: pc }}>
          <NewsTicker messages={tickerMessages} pc={pc} />
        </div>
      )}

      {/* ═══ HERO / PLAYER SECTION ═══ */}
      <section id="section-home" className="relative">
        {/* Hero Background Image */}
        {(config.page_hero_image || config.background_url || config.bg_image) && (
          <div className="absolute inset-0 z-0">
            <img
              src={config.page_hero_image || config.background_url || config.bg_image}
              alt="" className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
          </div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* Player Card */}
            <div className="lg:col-span-2" id="section-nowplaying">
              <div className="flex items-center justify-center">
                <StationPlayerCard
                  config={config} np={np} isPlaying={isPlaying} volume={volume}
                  isMuted={isMuted} listeners={listeners} mounts={mounts}
                  selectedMount={selectedMount} showMountDropdown={showMountDropdown}
                  setShowMountDropdown={setShowMountDropdown}
                  analyzerRef={analyzerRef} showVis={show.vis}
                  stationName={stationName} tagline={tagline} logo={logo}
                  isLive={isLive} pc={pc} tc={tc} cc={cc}
                  onTogglePlay={togglePlay} onVolumeChange={handleVolumeChange}
                  onMute={handleMute} onSwitchMount={switchMount}
                  onShare={handleShare} copied={copied}
                  showHistory={showHistory} setShowHistory={setShowHistory}
                  history={history}
                />
              </div>

              {/* Content sections fill below player — rendered in saved order */}
              {(() => {
                const order = config.section_order || ["chat","requests","about","history"];
                const mainSections = {
                  chat: show.chat && (
                    <ChatSection key="chat"
                      chatMessages={chatMessages} chatInput={chatInput}
                      setChatInput={setChatInput} onSendChat={sendChat}
                      user={user} token={token} pc={pc}
                    />
                  ),
                  requests: show.requests && (
                    <RequestsSection key="requests"
                      requestText={requestText} setRequestText={setRequestText}
                      dedicateText={dedicateText} setDedicateText={setDedicateText}
                      dedicateTo={dedicateTo} setDedicateTo={setDedicateTo}
                      onSendRequest={sendRequest} requestSuccess={requestSuccess}
                      showDedicate={show.dedicate} pc={pc}
                    />
                  ),
                  about: show.about && (
                    <AboutSection key="about" stationName={stationName} config={config} tagline={tagline} pc={pc} />
                  ),
                };
                return order.filter(id => mainSections[id]).map(id => mainSections[id]);
              })()}
            </div>

            {/* Right Sidebar — rendered in saved order */}
            <div className="space-y-4">
              {(() => {
                const order = config.section_order || ["weather","onAir","newsletter","liveVideo","sponsor","schedule","merch","donations","contact","socialLinks","topFans","launchCta"];
                const sidebarSections = {
                  weather: show.weather && <WeatherWidget key="weather" weather={weather} pc={pc} loading={weatherLoading} cityName={config.weather_city} forecast={weatherForecast} showBackground={config.weather_show_background !== false} tc={tc} cc={cc} />,
                  onAir: show.onAir && <OnAirWidget key="onAir" isLive={isLive} djName={djName} np={np} pc={pc} sc={sc} scheduledShow={onAirShow} tc={tc} cc={cc} />,
                  newsletter: show.newsletter && (
                    <FollowWidget key="newsletter"
                      isFollowing={isFollowing} user={user} followEmail={followEmail}
                      setFollowEmail={setFollowEmail} followLoading={followLoading}
                      onFollow={handleFollow} pc={pc} sc={sc} tc={tc} cc={cc}
                    />
                  ),
                  liveVideo: show.liveVideo && <LiveVideoSection key="liveVideo" videoUrl={config.video_url} pc={pc} />,
                  sponsor: show.sponsor && <SponsorWidget key="sponsor" config={config} pc={pc} tc={tc} cc={cc} />,
                  schedule: show.schedule && <ScheduleWidget key="schedule" pc={pc} tc={tc} cc={cc} shows={scheduledShows} onAirShowId={onAirShow?.show_id} onDJClick={djId => {
                    const dj = djProfiles.find(d => d.dj_id === djId);
                    if (dj) setViewingDJ(dj);
                  }} />,
                  merch: show.merch && <MerchWidget key="merch" pc={pc} tc={tc} cc={cc} />,
                  donations: show.donations && <DonationsWidget key="donations" pc={pc} tc={tc} cc={cc} />,
                  contact: show.contact && <ContactWidget key="contact" pc={pc} tc={tc} cc={cc} />,
                  socialLinks: (show.socialLinks && hasSocials) && <SocialLinksWidget key="socialLinks" socials={socials} tc={tc} cc={cc} />,
                  topFans: show.topFans && <TopFansWidget key="topFans" pc={pc} tc={tc} cc={cc} />,
                  launchCta: show.launchCta && <LaunchCtaWidget key="launchCta" pc={pc} sc={sc} tc={tc} cc={cc} />,
                };
                return order.filter(id => sidebarSections[id]).map(id => sidebarSections[id]);
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DJ PROFILE MODAL ═══ */}
      {viewingDJ && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setViewingDJ(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="dj-profile-modal">
            <div className="relative">
              {viewingDJ.image_url ? (
                <img src={viewingDJ.image_url} alt={viewingDJ.name} className="w-full h-48 object-cover" />
              ) : (
                <div className="w-full h-48 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${pc}33, ${pc}11)` }}>
                  <User className="w-16 h-16 text-white/20" />
                </div>
              )}
              <button onClick={() => setViewingDJ(null)} className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white/70 hover:text-white transition">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <h3 className="text-white text-xl font-bold">{viewingDJ.name}</h3>
              <p className="text-sm font-medium mt-0.5" style={{ color: pc }}>{viewingDJ.role || "DJ"}</p>
              {viewingDJ.bio && <p className="text-white/50 text-sm mt-3 leading-relaxed">{viewingDJ.bio}</p>}
              <div className="flex flex-wrap gap-2 mt-4">
                {viewingDJ.social_instagram && <a href={viewingDJ.social_instagram} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Instagram</a>}
                {viewingDJ.social_twitter && <a href={viewingDJ.social_twitter} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Twitter</a>}
                {viewingDJ.social_facebook && <a href={viewingDJ.social_facebook} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Facebook</a>}
                {viewingDJ.social_youtube && <a href={viewingDJ.social_youtube} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">YouTube</a>}
                {viewingDJ.social_tiktok && <a href={viewingDJ.social_tiktok} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">TikTok</a>}
                {viewingDJ.social_website && <a href={viewingDJ.social_website} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white transition">Website</a>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t py-6 px-4" style={{ borderColor: `${pc}15`, background: "rgba(0,0,0,0.35)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-sm" style={{ color: `${tc}4D` }}>{stationName}</span>
          <p className="text-xs" style={{ color: `${tc}26` }}>
            Powered by <a href="/" className="underline hover:opacity-60 transition">DJsandMCMedia</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StationPage;
